import React from 'react';

import { i18n } from 'config/locales';

import connectSubsection from 'components/Section/shared/SubsectionConnector';
import Subsection, { SubsectionProps } from 'components/Section/shared/Subsection';
import { FINANCIAL, FINANCIAL_ASSET_SEIZED } from 'config/formSections/financial';

import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Accordion from 'components/Form/Accordion';
import { DateSummary, Summary } from 'components/Summary';
import { FinancialAssetSeizedItemModel, FinancialAssetSeizedModel } from 'forms/sections/financial/assetSeized/types';
import { CommonErrorTypes, CommonOnError, OnUpdate } from 'types/props';
import { ListItemModel, ListModel, YesOrNoModel } from 'forms/shared/types';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import AssetSeizedItem from './AssetSeizedItem';

const sectionConfig = {
  key: FINANCIAL_ASSET_SEIZED.key,
  section: FINANCIAL.name,
  store: FINANCIAL.store,
  subsection: FINANCIAL_ASSET_SEIZED.name,
  storeKey: FINANCIAL_ASSET_SEIZED.storeKey,
};

interface Props extends Partial<FinancialAssetSeizedModel>, SubsectionProps {
  HasAssetSeized?: YesOrNoModel;
  List?: ListModel<FinancialAssetSeizedItemModel>;
  onError?: CommonOnError;
  onUpdate?: OnUpdate<FinancialAssetSeizedModel>;
  dispatch: () => void;
  defaultState: boolean;
  scrollToBottom: string;
  required?: boolean;
  scrollIntoView?: boolean;
  errors: CommonErrorTypes;
}

export class AssetSeized extends Subsection<Props> {
  constructor(props: Props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
    this.handleError = this.handleError.bind(this);
    this.summary = this.summary.bind(this);
  }

  static defaultProps = {
    HasAssetSeized: {},
    List: {},
    onError: (value: '', arr: ErrorCode[]) => arr,
    onUpdate: () => {},
    dispatch: () => {},
    defaultState: true,
    scrollToBottom: '.bottom-btns',
    required: true,
    scrollIntoView: true,
    errors: [],
  };

  summary = (item: ListItemModel<FinancialAssetSeizedItemModel>, index: number) => {
    const o = (item && item.Item) || {};
    const date = DateSummary(o.When);
    const seizureType = (o.SeizureType || {}).value || '';

    return Summary({
      type: i18n.t('financial.pvq.assetSeized.summary.type'),
      index,
      left: seizureType,
      right: date,
      placeholder: i18n.t('financial.pvq.assetSeized.summary.unknown'),
    });
  };

  update = (queue: FinancialAssetSeizedModel) => {
    this.props.onUpdate?.(this.storeKey, {
      HasAssetSeized: this.props.HasAssetSeized,
      List: this.props.List,
      ...queue,
    });
  };

  updateList = (values: FinancialAssetSeizedModel['List']) => {
    this.update({
      List: values,
    });
  };

  updateHasAssetSeized = (values: FinancialAssetSeizedModel['HasAssetSeized']) => {
    this.update({
      HasAssetSeized: values,
      List: values?.value === 'Yes' ? this.props.List : { items: [], branch: {} },
    });
  };

  render() {
    const {
      errors,
      required,
      scrollIntoView,
      scrollToBottom,
      defaultState,
      dispatch
    } = this.props;

    const accordionErrors = errors && errors.filter((e) => e?.indexOf?.('List.accordion') === 0);
    return (
      <div
        className="section-content assetseized"
        data-section={FINANCIAL.key}
        data-subsection={FINANCIAL_ASSET_SEIZED.key}
      >
        <h1 className="section-header">{i18n.t('financial.pvq.destination.assetseized')}</h1>
        <Branch
          name="has_assetseized"
          label={i18n.t('financial.pvq.assetSeized.branch.title')}
          labelSize="h4"
          className="asset-seized-branch"
          value={this.props.HasAssetSeized?.value}
          warning
          onUpdate={this.updateHasAssetSeized}
          required={required}
          scrollIntoView={scrollIntoView}
          onError={this.handleError}
          attribute="HasAssetSeized"
        />
        <Show when={(this.props.HasAssetSeized || {}).value === 'Yes'}>
          <Accordion
            branch={this.props.List?.branch}
            items={this.props.List?.items}
            defaultState={defaultState}
            scrollToBottom={scrollToBottom}
            onUpdate={this.updateList}
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            required={required}
            summary={this.summary}
            errors={accordionErrors}
            scrollIntoView={scrollIntoView}
            appendTitle={i18n.t('financial.pvq.assetSeized.appendTitle')}
            attribute="List"
          >
            <AssetSeizedItem
              name="Item"
              dispatch={dispatch}
              required={required}
              scrollIntoView={scrollIntoView}
              bind
              attribute="List"
            />
          </Accordion>
        </Show>
      </div>
    );
  }
}

export default connectSubsection(AssetSeized, sectionConfig);
